// src/components/OurStory.js
import React, { useEffect, useRef, useState } from 'react'
import { Container, Row, Col, Accordion, Alert } from 'react-bootstrap'
import FloatingActionButton from './FloatingActionButton'
import './OurStory.css'
import { useNavigate } from 'react-router-dom'
import { useContent } from '../hooks/useContent'
import Footer from './footer'
import rooted from '../assets/imgDisp/rooted.jpg'
import shola from '../assets/imgDisp/shola.jpg'
import waterl from '../assets/imgDisp/waterL.jpg'
import waters from '../assets/imgDisp/waterS.jpg'
import zero from '../assets/imgDisp/zero.jpg'


const OurStory = () => {
  const navigate = useNavigate()
  const { content, loading, error } = useContent('ourStory')

  // Lightweight lazy background image component to defer heavy media until in-view
  const LazyBackground = ({ className, src, style, children }) => {
    const ref = useRef(null)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
      const el = ref.current
      if (!el || !src) {
        setLoaded(true)
        return
      }

      // Fallback if IntersectionObserver isn't available
      if (typeof IntersectionObserver === 'undefined') {
        setLoaded(true)
        return
      }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setLoaded(true)
              io.disconnect()
            }
          })
        },
        { rootMargin: '200px 0px', threshold: 0.01 }
      )
      io.observe(el)
      return () => io.disconnect()
    }, [src])

    const bgStyle = loaded && src ? { backgroundImage: `url(${src})` } : {}
    return (
      <div ref={ref} className={className} style={{ ...(style || {}), ...bgStyle }}>
        {children}
      </div>
    )
  }

  // Use content with fallbacks
  const heroContent = content?.hero || {
    title: 'Our Story',
    subtitle: 'Where It All Began',
    backgroundImage: '',
  }
  const dreamContent = content?.dreamSection || {
    title: 'Rooted in a Dream',
    paragraphs: [],
    stats: [],
    image: '',
  }

  const farmingContent = content?.farmingPurpose || {
    title: 'Farming with Purpose',
    subtitle: '',
    accordionItems: [],
  }
  const ctaContent = content?.cta || {
    title: 'Join Our Conservation Journey',
    description: '',
    primaryButtonText: 'Book Your Stay',
    secondaryButtonText: 'Support Our Mission',
  }

  // Fallback images for other sections
  const defaultStoryImage = rooted
    const secondStoryImage =  [shola, waterl, waters, zero]

  // Dream section images (supports new images array with backward compatibility)
  const dreamImages = (
    dreamContent.images && dreamContent.images.length > 0
      ? dreamContent.images
      : (dreamContent.image ? [dreamContent.image] : [defaultStoryImage])
  )
  const [dreamIndex, setDreamIndex] = useState(0)
  useEffect(() => {
    if (!dreamImages || dreamImages.length <= 1) return
    const id = setInterval(() => {
      setDreamIndex((i) => (i + 1) % dreamImages.length)
    }, 7000)
    return () => clearInterval(id)
  }, [dreamImages])

  // Hero image - use CMS image only (no fallback)
  const heroBg = heroContent?.backgroundImage || ''
  
  // Removed loading spinner: render page immediately with fallback content.

  // Show error state
  if (error) {
    return (
      <div className='story-page'>
        <Alert variant='danger' className='text-center mb-0'>
          <strong>Content Loading Error:</strong> {error}
          <p className='mt-2'>Please try refreshing the page.</p>
        </Alert>
      </div>
    )
  }

  

  return (
    <div className='story-page'>
      {/* Hero Section */}
     <section className='hero-section'>
  <img 
    src={heroBg} 
    alt="Our Story Hero" 
    className='hero-image'
    loading='eager'
    fetchpriority='high'
    decoding='async'
    style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }}
  />
  <div className='hero-overlay is-loaded'>
    <div className='hero-content'>
      <h1 className='hero-title'>{heroContent.title}</h1>
      <p className='hero-subtitle'>{heroContent.subtitle}</p>
    </div>
  </div>
</section>


      {/* Rooted in a Dream */}
      <section className='dream-section py-5'>
        <Container>
          <Row className='align-items-center'>
            <Col md={6} className='mb-md-0'>
              <div className='story-image-container'>
                <LazyBackground
                  className='story-image rounded-lg'
                  src={dreamImages[dreamIndex] || defaultStoryImage}
                />
              </div>
            </Col>
            <Col md={6}>
              <h2 className='section-head'>{dreamContent.title}</h2>
              {dreamContent.paragraphs.map((paragraph, index) => (
                <p key={index} className='story-text'>
                  {paragraph}
                </p>
              ))}
            </Col>
          </Row>
        </Container>
      </section>


      {/* Farming with Purpose */}
      <section className='farming-section py-5'>
        <Container>
          <div className='text-center mb-5'>
            <h2 className='section-head'>{farmingContent.title}</h2>
            <p className='lead'>{farmingContent.subtitle}</p>
          </div>

          <Accordion defaultActiveKey={['0', '1', '2', '3']} alwaysOpen
            className='purpose-accordion'>
            {farmingContent.accordionItems.map((item, index) => (
              <Accordion.Item eventKey={String(index)} key={index}>
                <Accordion.Header>
                  <div className='accordion-icon me-3'>{item.icon}</div>
                  <h3 className='accordHead'>{item.title}</h3>
                </Accordion.Header>
                <Accordion.Body>
                  <Row className='align-items-center'>
                    <Col md={6} className={index % 2 === 1 ? 'order-md-2' : ''}>
                      <p>{item.content}</p>
                      {item.benefits && item.benefits.length > 0 && (
                        <ul className='impact-list'>
                          {item.benefits.map((benefit, bIndex) => (
                            <li key={bIndex}>{benefit}</li>
                          ))}
                        </ul>
                      )}
                    </Col>
                    <Col md={6} className={index % 2 === 1 ? 'order-md-1' : ''}>
                      <div className='story-image-container mt-4 mt-md-0'>
                        <LazyBackground
                          className='story-image rounded-lg'
                          src={item.image || secondStoryImage[index % secondStoryImage.length]}
                        />
                      </div>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* Call to Action */}
      <section className='sectionCta py-5 text-center'>
        <Container>
          <h2 className='section-head'>{ctaContent.title}</h2>
          <p className='cta-text mb-4'>{ctaContent.description}</p>
          <button
            // size='lg'
            className='ourstory-book-btn'
            onClick={() => navigate('/stays')}
          >
            {ctaContent.primaryButtonText}
          </button>
        </Container>
      </section>

      {/* Footer */}
      <Footer />
    

      <FloatingActionButton />
    </div>
  )
}

export default OurStory
